import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = [
  { id: 'balanced', label: 'Balanced', description: 'Sage reads the moment and adapts' },
  { id: 'gentle', label: 'Gentler', description: 'More warmth and reassurance, softer pacing' },
  { id: 'direct', label: 'More Direct', description: 'Straight to the point, honest and efficient' },
  { id: 'analytical', label: 'More Analytical', description: 'Pattern analysis and clear reasoning first' },
];

export const SageStyleSection = ({ token }) => {
  const [selected, setSelected] = useState('balanced');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/user/sage-style`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setSelected(res.data.style || 'balanced'))
      .catch(() => {});
  }, [token]);

  const handleSelect = async (styleId) => {
    if (styleId === selected || saving) return;
    const previous = selected;
    setSelected(styleId);
    setSaving(true);
    try {
      await axios.post(`${API}/user/sage-style`, { style: styleId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sage will adjust from your next message');
    } catch {
      setSelected(previous);
      toast.error('Failed to save style');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8" data-testid="sage-style-section">
      <div className="flex items-center space-x-3 mb-2">
        <MessageCircle className="w-6 h-6 text-stone-600" strokeWidth={1.5} />
        <h2 className="text-2xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
          Your Sage
        </h2>
      </div>
      <p className="text-sm text-stone-500 mb-6">
        Choose how Sage talks with you. You can change this anytime.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {STYLES.map((style) => {
          const isActive = selected === style.id;
          return (
            <button
              key={style.id}
              onClick={() => handleSelect(style.id)}
              disabled={saving}
              aria-pressed={isActive}
              className={`text-left p-4 rounded-xl border transition-all duration-200 ${
                isActive
                  ? 'border-[#C9A84C] bg-[#C9A84C]/10 shadow-sm'
                  : 'border-stone-200 bg-white/40 hover:border-stone-300'
              }`}
              data-testid={`sage-style-${style.id}`}
            >
              <p className={`font-medium ${isActive ? 'text-stone-900' : 'text-stone-700'}`}>
                {style.label}
                {isActive && <span className="ml-2 text-xs" style={{ color: '#C9A84C' }}>Active</span>}
              </p>
              <p className="text-xs text-stone-500 mt-1">{style.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};
